// What the v1 screen code (app.js) needs from the outside: the signed-in person, their ledger on start,
// the server calls it used to make to Apps Script, and the price service.
import { sb } from '../lib/supabase';
import { supabaseStore } from '../lib/store';
import { fromServer, toDataFile } from '../core/importer';
import { openImportDialog } from './importDialog';
import { deleteAccountFlow } from './account';
import { listBackups, readBackup, saveToDrive, setDriveUser } from './drive';

export interface Boot {
  rev: number; doc: Record<string, unknown> | null; history: unknown[]; intraday: unknown[]; savedAt: string | null;
  manualPrices: Record<string, unknown>; fxManual: unknown; divCheck: unknown; snapCheck: unknown;
  keys: { finnhubSet: boolean }; retentionHours: number; offline?: boolean;
}

async function rpc<T>(fn: string, args: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await sb().rpc(fn, args);
  if (error) throw new Error(error.message);
  return data as T;
}

/** The last dividend check and the last hourly check, as the server jobs left them. */
async function statuses(): Promise<{ divCheck: unknown; snapCheck: unknown }> {
  const { data, error } = await sb().from('job_status').select('kind,data').in('kind', ['divCheck', 'snapCheck']);
  if (error) return { divCheck: null, snapCheck: null };
  const m = Object.fromEntries((data || []).map((r: { kind: string; data: unknown }) => [r.kind, r.data]));
  return { divCheck: m.divCheck ?? null, snapCheck: m.snapCheck ?? null };
}

async function fn<T>(name: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await sb().functions.invoke(name, { body });
  if (error) {
    let msg = error.message;
    try { const j = await (error as { context?: Response }).context?.json(); if (j && j.error) msg = j.error; } catch { /* keep the generic message */ }
    throw new Error(msg);
  }
  return data as T;
}

// ---------- the copy kept on this device, for opening offline (PWA) ----------
const bootKey = (uid: string) => 'pl-boot:' + uid;
function keepBoot(uid: string, b: Boot) { try { localStorage.setItem(bootKey(uid), JSON.stringify(b)); } catch { /* storage full or blocked: offline opening just isn't available */ } }
function patchKeptBoot(uid: string, p: Partial<Boot>) {
  try { const r = localStorage.getItem(bootKey(uid)); if (r) localStorage.setItem(bootKey(uid), JSON.stringify({ ...JSON.parse(r), ...p })); } catch { /* as above */ }
}
/** The ledger as last loaded or saved on this device, for when the server can't be reached. The app
 *  then runs from the copy it keeps on this device (including changes not yet saved), and saves them
 *  once it's back online, with the usual check that no other device changed the ledger meanwhile. */
export function offlineBoot(uid: string): Boot | null {
  try {
    const r = localStorage.getItem(bootKey(uid)), local = localStorage.getItem('pf-ledger-v1:' + uid);
    if (!r || !local) return null;
    const l = JSON.parse(local);
    if (!((l.txns && l.txns.length) || (l.cash && l.cash.length))) return null;   // never run offline from an empty copy
    return { ...JSON.parse(r), doc: null, offline: true };
  } catch { return null; }
}

export async function loadBoot(): Promise<Boot> {
  const [r, s, st] = await Promise.all([supabaseStore.load(), rpc<{ retentionHours: number; finnhubSet: boolean }>('my_settings'), statuses()]);
  const boot: Boot = {
    rev: r.rev, doc: r.doc, history: r.history, intraday: r.intraday, savedAt: r.savedAt,
    manualPrices: r.manualPrices, fxManual: r.fxManual, ...st,
    keys: { finnhubSet: !!s?.finnhubSet }, retentionHours: s?.retentionHours ?? 25,
  };
  const { data } = await sb().auth.getSession();
  if (data.session) keepBoot(data.session.user.id, boot);
  return boot;
}

export function makeEnv(uid: string, email: string, boot: Boot) {
  const api = {
    // v1's saveState: { baseRev, doc (JSON text or null), history, replaceHistory }
    async saveState(p: { baseRev: number; doc: string | null; history: unknown[]; replaceHistory: boolean }) {
      const r = await rpc<Record<string, unknown>>('save_ledger', { p_base_rev: p.baseRev, p_doc: p.doc == null ? null : JSON.parse(p.doc), p_history: p.history, p_replace_history: !!p.replaceHistory });
      if (!r.conflict) patchKeptBoot(uid, { rev: r.rev as number, ...(p.doc != null ? { doc: JSON.parse(p.doc) } : {}) });
      return r.conflict ? { ...r, ...(await statuses()) } : r;
    },
    async loadState() {
      const r = await supabaseStore.load();
      patchKeptBoot(uid, { rev: r.rev, doc: r.doc, history: r.history, intraday: r.intraday, savedAt: r.savedAt });
      return { ...r, ...(await statuses()) };
    },
    // Dividend updates from the daily check, waiting to be applied (the old app's files in Drive).
    async getInbox() {
      const { data, error } = await sb().from('dividend_inbox').select('id,name,content').eq('done', false).order('created_at').limit(20);
      if (error) throw new Error(error.message);
      return (data || []).map((r: { id: string; name: string; content: string }) => ({ id: r.id, name: r.name, text: r.content }));
    },
    async finishInbox(ids: string[]) { await rpc('finish_inbox', { p_ids: ids || [] }); return true; },
    async runDividendCheck() { return fn('dividends', { action: 'check' }); },
    async pastDividends(list: unknown[]) { return fn('dividends', { action: 'past', list }); },
    async setManualPrice(key: string, data: unknown) { await rpc('set_manual_price', { p_key: key, p_data: data }); },
    async setFxManual(fx: unknown) { await rpc('set_fx_manual', { p_fx: fx }); },
    async eraseExtras() { await rpc('erase_extras'); },
    async setFinnhubKey(key: string) { return rpc<boolean>('set_finnhub_key', { p_key: key }); },
    async exportAll() { const r = await supabaseStore.load(); return toDataFile(fromServer(r), r.rev, r.savedAt); },
  };
  const prices = {
    async refresh(req: Record<string, unknown>) { return fn('prices', req); },
  };
  setDriveUser(email);
  const drive = { save: saveToDrive, list: listBackups, read: readBackup };
  return {
    uid, email, boot, api, prices, drive,
    openImport: () => openImportDialog(),
    reload: () => location.reload(),
    deleteAccount: (opts?: { backup?: () => void }) => deleteAccountFlow(opts),
    signOut: () => sb().auth.signOut(),
  };
}
